import { memo, useEffect, useMemo, useRef } from 'react';

import { useTranslate } from '@/hooks/useTranslate';
import { EventNewID, ViralTemplateExposeFromEnum } from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';

import TemplateCard from '../../template-ui/TemplateCard';
import { TemplateCardSkeletonCell } from '../../template-ui/TemplateCardSkeleton';
import {
  templateCardStaggerFadeClassName,
  templateCardStaggerFadeStyle,
} from '../../template-ui/TemplateMotion';
import type { TplTemplateVO } from '../../template-ui/types';

/** 与原先的底部骨架列数一致 */
const LOAD_MORE_SKELETON_COUNT = 6;
const VIRAL_TEMPLATE_EXPOSE_DEBOUNCE_MS = 200;
const exposedTemplateKeys = new Set<string>();

interface TemplateGridProps {
  items: TplTemplateVO[];
  loading: boolean;
  from: ViralTemplateExposeFromEnum;
  isLoadingMore?: boolean;
  skeletonCount?: number;
  /** 固定列数（用于一级预览“两行”严格对齐）。不传则使用 auto-fill */
  columns?: number;
  cardWidth?: number;
  gap?: number;
  rowHeight?: number;
  onReplicate?: (tpl: TplTemplateVO) => void;
  /** 传入则启用滚动容器与无限滚动触底监听 */
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  onScroll?: () => void;
}

const DEFAULT_SKELETON_COUNT = 12;

/**
 * 单格数据：真实卡片或骨架占位（类似 Ant Design List + Skeleton：同一套 map，维护一份结构）
 */
type TemplateGridCell =
  | { kind: 'template'; key: string; template: TplTemplateVO; visualIndex: number }
  | { kind: 'skeleton'; key: string }
  /** 加载更多前与列表区的间距（等同原先外包 div 的 pt-[15px]） */
  | { kind: 'spacer'; key: string };

function buildGridCells(
  items: TplTemplateVO[],
  loading: boolean,
  isLoadingMore: boolean | undefined,
  skeletonCount: number,
): TemplateGridCell[] {
  if (loading && items.length === 0) {
    return Array.from({ length: skeletonCount }, (_, i) => ({
      kind: 'skeleton' as const,
      key: `tpl-skeleton-initial-${i}`,
    }));
  }

  const rows: TemplateGridCell[] = items.map((tpl, idx) => ({
    kind: 'template' as const,
    key: String(tpl.id),
    template: tpl,
    visualIndex: idx,
  }));

  if (isLoadingMore && items.length > 0) {
    rows.push({ kind: 'spacer', key: 'tpl-grid-load-more-gap' });
    for (let i = 0; i < LOAD_MORE_SKELETON_COUNT; i++) {
      rows.push({
        kind: 'skeleton',
        key: `tpl-skeleton-more-${i}`,
      });
    }
  }

  return rows;
}

const buildExposeKey = (templateId: number, from: ViralTemplateExposeFromEnum) =>
  `${from}:${templateId}`;

const TemplateGrid = memo(function TemplateGrid({
  items,
  loading,
  from,
  isLoadingMore,
  skeletonCount = DEFAULT_SKELETON_COUNT,
  columns,
  cardWidth = 180,
  gap = 15,
  rowHeight = 320,
  onReplicate,
  scrollContainerRef,
  onScroll,
}: TemplateGridProps) {
  const { t } = useTranslate();
  const innerRef = useRef<HTMLDivElement>(null);
  const templateElementMapRef = useRef(new Map<string, HTMLDivElement>());
  const pendingExposeTimersRef = useRef(new Map<string, number>());
  const visibleExposeKeysRef = useRef(new Set<string>());

  const containerRef = scrollContainerRef || innerRef;
  const isEmpty = useMemo(() => !loading && items.length === 0, [loading, items.length]);

  const gridCells = useMemo(
    () => buildGridCells(items, loading, isLoadingMore, skeletonCount),
    [items, loading, isLoadingMore, skeletonCount],
  );
  const templateCells = useMemo(
    () => gridCells.filter((cell) => cell.kind === 'template'),
    [gridCells],
  );

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns:
        typeof columns === 'number' && columns > 0
          ? `repeat(${columns}, ${cardWidth}px)`
          : `repeat(auto-fill, ${cardWidth}px)`,
      gridAutoRows: `${rowHeight}px`,
      gap,
    }),
    [columns, cardWidth, rowHeight, gap],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (templateCells.length === 0) return;

    const clearPendingTimer = (exposeKey: string) => {
      const timer = pendingExposeTimersRef.current.get(exposeKey);
      if (timer === undefined) return;
      window.clearTimeout(timer);
      pendingExposeTimersRef.current.delete(exposeKey);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target as HTMLDivElement;
          const exposeKey = target.dataset.exposeKey;
          const templateId = Number(target.dataset.templateId);

          if (!exposeKey || !Number.isFinite(templateId)) return;

          if (!entry.isIntersecting || entry.intersectionRatio <= 0) {
            visibleExposeKeysRef.current.delete(exposeKey);
            clearPendingTimer(exposeKey);
            return;
          }

          visibleExposeKeysRef.current.add(exposeKey);

          if (
            exposedTemplateKeys.has(exposeKey) ||
            pendingExposeTimersRef.current.has(exposeKey)
          ) {
            return;
          }

          const timer = window.setTimeout(() => {
            pendingExposeTimersRef.current.delete(exposeKey);
            if (!visibleExposeKeysRef.current.has(exposeKey)) return;
            if (exposedTemplateKeys.has(exposeKey)) return;

            exposedTemplateKeys.add(exposeKey);
            trackEvent(EventNewID.VIRAL_TEMPLATE_EXPOSE, {
              template_id: templateId,
              from,
            });
          }, VIRAL_TEMPLATE_EXPOSE_DEBOUNCE_MS);

          pendingExposeTimersRef.current.set(exposeKey, timer);
        });
      },
      {
        threshold: 0.25,
      },
    );

    templateCells.forEach((cell) => {
      const exposeKey = buildExposeKey(cell.template.id, from);
      const element = templateElementMapRef.current.get(exposeKey);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
      visibleExposeKeysRef.current.clear();
      pendingExposeTimersRef.current.forEach((timer) => {
        window.clearTimeout(timer);
      });
      pendingExposeTimersRef.current.clear();
    };
  }, [from, templateCells]);

  const renderCell = (cell: TemplateGridCell) => {
    if (cell.kind === 'template') {
      const exposeKey = buildExposeKey(cell.template.id, from);
      return (
        <div
          key={cell.key}
          className={templateCardStaggerFadeClassName}
          style={templateCardStaggerFadeStyle(cell.visualIndex)}
          data-template-id={cell.template.id}
          data-expose-key={exposeKey}
          ref={(node) => {
            if (node) {
              templateElementMapRef.current.set(exposeKey, node);
              return;
            }
            templateElementMapRef.current.delete(exposeKey);
          }}
        >
          <TemplateCard template={cell.template} onReplicate={onReplicate} />
        </div>
      );
    }

    if (cell.kind === 'spacer') {
      return (
        <div key={cell.key} className="col-span-full h-[15px] shrink-0" aria-hidden />
      );
    }

    return (
      <div key={cell.key}>
        <TemplateCardSkeletonCell />
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={
        scrollContainerRef
          ? 'mt-[12px] flex-1 overflow-y-auto overflow-x-hidden no-scrollbar'
          : ''
      }
      onScroll={onScroll}
    >
      {gridCells.length > 0 && (
        <div
          className={
            loading && items.length === 0 ? 'min-h-[280px] grid w-full' : 'grid w-full'
          }
          style={gridStyle}
        >
          {gridCells.map(renderCell)}
        </div>
      )}

      {isEmpty && (
        <div className="flex min-h-[280px] w-full items-center justify-center text-[14px] text-[#11111199]">
          {t('Oops-nothing-here')}
        </div>
      )}
    </div>
  );
});

TemplateGrid.displayName = 'TemplateGrid';

export default TemplateGrid;
